import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { talkImageMode } from "../sectionLayout";
import "../css/TalkPrint.css";

/* ── HTML normalizer (same logic as talk.js) ──────────────────────────────── */
function mergeAndNormalizeHTML(htmlParts) {
    const container = document.createElement("div");
    htmlParts.forEach((html) => {
        if (!html) return;
        const tmp = document.createElement("div");
        tmp.innerHTML = html;
        while (tmp.firstChild) container.appendChild(tmp.firstChild);
    });

    Array.from(container.querySelectorAll("p")).forEach((p) => {
        const onlyBr = p.children.length === 1 && p.firstElementChild?.tagName === "BR";
        if (onlyBr || p.textContent.trim() === "") p.remove();
    });

    function liLevel(li) {
        const m = (li.className || "").match(/ql-indent-(\d+)/);
        return m ? parseInt(m[1], 10) : 0;
    }
    function liType(li, parentTag) {
        const t = li.getAttribute("data-ql-list") || li.getAttribute("data-list");
        if (t === "bullet") return "ul";
        if (t === "ordered") return "ol";
        return parentTag.toLowerCase();
    }
    function cleanLi(li) {
        const newLi = document.createElement("li");
        Array.from(li.childNodes).forEach((child) => {
            if (child.nodeType === 1 && child.classList.contains("ql-ui")) return;
            newLi.appendChild(child.cloneNode(true));
        });
        newLi.removeAttribute("data-ql-list");
        newLi.removeAttribute("data-list");
        newLi.className = (li.className || "").replace(/\bql-indent-\d+\b/g, "").trim();
        return newLi;
    }
    function reconstructList(listNode) {
        const parentTag = listNode.tagName.toLowerCase();
        const lis = Array.from(listNode.querySelectorAll(":scope > li"));
        const frag = document.createDocumentFragment();
        const stack = [{ node: frag, level: -1 }];
        lis.forEach((li) => {
            const level = liLevel(li);
            const type = liType(li, parentTag);
            while (stack[stack.length - 1].level >= level) stack.pop();
            let top = stack[stack.length - 1];
            let currentList = top.node.lastElementChild;
            if (!currentList || currentList.tagName.toLowerCase() !== type) {
                currentList = document.createElement(type);
                top.node.appendChild(currentList);
            }
            stack.push({ node: currentList, level });
            currentList.appendChild(cleanLi(li));
        });
        listNode.replaceWith(frag);
    }
    Array.from(container.querySelectorAll("ol, ul")).forEach(reconstructList);

    // Merge adjacent same-type lists
    let node = container.firstChild;
    while (node) {
        const next = node.nextSibling;
        if (next && node.nodeType === 1 && next.nodeType === 1 &&
            (node.tagName === "UL" || node.tagName === "OL") &&
            next.tagName === node.tagName) {
            while (next.firstChild) node.appendChild(next.firstChild);
            next.remove();
            continue;
        }
        node = next;
    }
    return container.innerHTML;
}

/* ── Section renderer ─────────────────────────────────────────────────────── */
function PrintSection({ section, index }) {
    const type = (section?.type || "content").toLowerCase();

    if (type === "divider") {
        return (
            <div className="pdf-divider">
                <h2 className="pdf-divider-title">{section.header}</h2>
            </div>
        );
    }

    const parts = [];
    if (section?.body) parts.push(section.body);
    if (Array.isArray(section?.bodies)) section.bodies.forEach((p) => { if (p) parts.push(p); });
    const html = mergeAndNormalizeHTML(parts);

    const imageUrl = (section?.imageUrl || "").trim();
    const hasText = html.trim() !== "";
    const mode = imageUrl ? talkImageMode(section) : "text";
    const isGraphic = imageUrl && (mode === "image" || (mode === "both" && !hasText));
    const isHybrid = imageUrl && mode === "both" && hasText;

    if (isGraphic && imageUrl) {
        return (
            <div className="pdf-section pdf-section--graphic">
                {section.header && <h2 className="pdf-section-title">{section.header}</h2>}
                <figure className="pdf-graphic-figure">
                    <img src={imageUrl} alt={section.imageAlt || section.header || ""} className="pdf-graphic-img" />
                    {section.imageCaption && <figcaption className="pdf-graphic-caption">{section.imageCaption}</figcaption>}
                </figure>
            </div>
        );
    }

    if (isHybrid && imageUrl) {
        return (
            <div className="pdf-section pdf-section--hybrid">
                {section.header && <h2 className="pdf-section-title">{section.header}</h2>}
                <div className="pdf-hybrid-grid">
                    <div className="pdf-hybrid-text" dangerouslySetInnerHTML={{ __html: html }} />
                    <figure className="pdf-hybrid-figure">
                        <img src={imageUrl} alt={section.imageAlt || section.header || ""} className="pdf-hybrid-img" />
                        {section.imageCaption && <figcaption className="pdf-graphic-caption">{section.imageCaption}</figcaption>}
                    </figure>
                </div>
            </div>
        );
    }

    return (
        <div className="pdf-section">
            {section.header && <h2 className="pdf-section-title">{section.header}</h2>}
            {html && <div className="pdf-body" dangerouslySetInnerHTML={{ __html: html }} />}
        </div>
    );
}

/* ── Main component ───────────────────────────────────────────────────────── */
export default function TalkPrint() {
    const { talkId } = useParams();
    const [talk, setTalk] = useState(null);
    const [loading, setLoading] = useState(true);
    const printed = useRef(false);

    useEffect(() => {
        if (!talkId) return;
        getDoc(doc(db, "talks", talkId)).then((snap) => {
            if (snap.exists()) setTalk(snap.data());
            setLoading(false);
        });
    }, [talkId]);

    // Auto-print once images have loaded
    useEffect(() => {
        if (loading || !talk || printed.current) return;
        printed.current = true;
        // Small delay so fonts & images settle
        const timer = setTimeout(() => window.print(), 800);
        return () => clearTimeout(timer);
    }, [loading, talk]);

    if (loading) {
        return <div className="pdf-loading">Preparing document…</div>;
    }
    if (!talk) {
        return <div className="pdf-loading">Talk not found.</div>;
    }

    const { title, verseText, verseRef, sections = [], references, referencesBodies } = talk;
    const year = new Date().getFullYear();

    const refParts = [];
    if (typeof references === "string" && references.trim()) refParts.push(references);
    if (Array.isArray(referencesBodies)) referencesBodies.forEach((p) => { if (p) refParts.push(p); });
    const refHTML = refParts.length ? mergeAndNormalizeHTML(refParts) : "";

    return (
        <div className="pdf-root">
            {/* ── Cover / first-page header ── */}
            <header className="pdf-header">
                <div className="pdf-header-brand">
                    <img src="/SOLE-logo.svg" alt="SOLE Ministries" className="pdf-logo" />
                    <div className="pdf-header-brand-text">
                        <span className="pdf-brand-name">SOLE Ministries</span>
                        <span className="pdf-brand-tagline">Servants of Our Lady of Evangelization</span>
                    </div>
                </div>
                <div className="pdf-header-divider" />
                <h1 className="pdf-title">{title}</h1>
                {(verseText || verseRef) && (
                    <blockquote className="pdf-verse">
                        {verseText && <p className="pdf-verse-text">&#8220;{verseText}&#8221;</p>}
                        {verseRef && <cite className="pdf-verse-ref">— {verseRef}</cite>}
                    </blockquote>
                )}
            </header>

            {/* ── Sections ── */}
            <main className="pdf-main">
                {sections.map((sec, i) => (
                    <PrintSection key={i} section={sec} index={i} />
                ))}

                {/* ── References ── */}
                {refHTML && (
                    <div className="pdf-section pdf-references">
                        <h2 className="pdf-section-title pdf-references-title">References</h2>
                        <div className="pdf-body pdf-references-body" dangerouslySetInnerHTML={{ __html: refHTML }} />
                    </div>
                )}

                {/* ── Document footer ── */}
                <div className="pdf-doc-footer" aria-hidden="true">
                    <span>SOLE Ministries &nbsp;·&nbsp; soleministries.org</span>
                    <span>© {year} SOLE Ministries</span>
                </div>
            </main>

            {/* ── Screen-only controls ── */}
            <div className="pdf-screen-controls">
                <button className="pdf-print-btn" onClick={() => window.print()}>
                    Print / Save as PDF
                </button>
                <button className="pdf-close-btn" onClick={() => window.close()}>
                    Close
                </button>
                <span className="pdf-hint">
                    In the print dialog: uncheck <strong>Headers and footers</strong>, then click <strong>Save</strong>.
                </span>
            </div>
        </div>
    );
}
